import React, { MutableRefObject } from 'react';
import classNames from 'classnames';
import { Button } from '@capcenter/shared';
import { ScheduleTourDateProps  } from '../../../interfaces/interfaces';

const DatePicker = (props: {
  leftArrowActive: boolean, 
  setLeftArrowActive: (value: boolean) => void,
  rightArrowActive: boolean, 
  setRightArrowActive: (value: boolean) => void,
  selectedDate: object | undefined, 
  setSelectedDate: (value: any) => void,
  setShowErrorMessage: (value: boolean) => void,
  currentTimeslot: number | undefined,
  selectedCardIndex: number | undefined,
	setSelectedCardIndex: (value: number) => void,
	hideArrows?: boolean;
}) => {
	const ref = React.useRef() as MutableRefObject<HTMLDivElement>;
	const selectedDate = props.selectedDate;
	const setSelectedDate = props.setSelectedDate;
	const setSelectedCardIndex = props.setSelectedCardIndex;
	const datesArray: any = [];

	function getDates() {
		// Start with today's date if currentTimeslot < 2, otherwise start with tomorrow's date
		const dateToStart = props.currentTimeslot ? (props.currentTimeslot < 2 ? 0 : 1) : 0;
    
		// Get total of 7 dates
		const dateToStop = dateToStart + 6;
    
		for (let i = dateToStart; i <= dateToStop; i++) {
			const date = new Date();
			date.setDate(date.getDate() + i);
			datesArray.push( formatDate(date) );
		}
	}

	function formatDate(date: any) {
		let dateNumeric = date.getDate();
		const month: string = date.toLocaleString('default', { month: 'short' });
		let monthNumeric = date.getMonth() + 1;
		const weekday: string = date.toLocaleDateString('default', { weekday: 'short' });
		const year: string = date.getFullYear();
		if (dateNumeric < 10) { dateNumeric = '0' + dateNumeric; }
		if (monthNumeric < 10) { monthNumeric = '0' + monthNumeric; }

		const dateObject: ScheduleTourDateProps = {
			day: dateNumeric,
			month: month,
			monthNumeric: monthNumeric,
			weekday: weekday,
			year: year,
		};

		return dateObject;
	}

	// Adds/removes event listeners when scrolling
	React.useEffect(() => {
		const container = ref.current;  // copy 'ref.current' to local variable to avoid console warnings
		watchScrolling();
		container.addEventListener('scroll', watchScrolling);
		return () => container.removeEventListener('scroll', watchScrolling);
	});

	// Controls active state of left/right chevron buttons
	function watchScrolling() {

    
		// If container NOT scrolled all the way left or right
		// Max 'scrollLeft' value = 75% width of parent container 
		// (or 3 date cards + margins/padding)
		const parentBoundingBox = ref.current.getBoundingClientRect().width * 0.75;

		if (ref.current.scrollLeft > 0 && ref.current.scrollLeft < parentBoundingBox) {
			props.setLeftArrowActive(true);
			props.setRightArrowActive(true);
		}
    
		// If container scrolled all the way left
		if (ref.current.scrollLeft === 0) {
			props.setLeftArrowActive(false);
		}

		// If container scrolled all the way right
		if (ref.current.scrollLeft >= parentBoundingBox) {
			props.setRightArrowActive(false);
		}
	}

	// Slide date cards left/right
	function slide(direction: 'left' | 'right') {
		let scrollCompleted = 0; // no scroll distance yet
    
		const slideVar = setInterval(function () {
			if (direction === 'left') {
				ref.current.scrollLeft -= 40;
			} else {
				ref.current.scrollLeft += 40;
			}
			scrollCompleted += 40; // add distance to total

			if (scrollCompleted >= 250) { // right-side of operator = total distance scrolled in 'px'
				window.clearInterval(slideVar);
			}
		}, 53); // last value changes speed of scroll
	}

	// Default selected date to first available date
	React.useEffect(() => {
		if (selectedDate === undefined) {
			setSelectedDate({
				day: datesArray[0].day,
				month: datesArray[0].month,
				monthNumeric: datesArray[0].monthNumeric,
				weekday: datesArray[0].weekday,
				year: datesArray[0].year,
			});
			setSelectedCardIndex(0);
		}
	}, [selectedDate, setSelectedDate, setSelectedCardIndex, datesArray]);


	const DateCard = (props: {
    index: number,
    month: string,
    day: number,
    weekday: string,
    selectedCardIndex: number | undefined,
    onClick: () => void,
  }) => {
		return (
			<div className={classNames('schedule-tour__date-card d-flex flex-wrap border-radius text-center fw-bold ripple ripple--dark', props.selectedCardIndex === props.index && 'selected')} onClick={props.onClick}>
				<div className="date-card__month w-100">
					{props.month}
				</div>
				<div className="date-card__date w-100">
					{props.day}
				</div>
				<hr className="date-card__hr w-75 my-0" />
				<div className="date-card__weekday w-100 mt-1">
					{props.weekday}
				</div>
			</div>
		);
	};

	getDates();
  
	return (
		<div className={'schedule-tour__date-picker row g-0 flex-nowrap align-items-center'}>
			{!props.hideArrows && (
				<div className={classNames((props.leftArrowActive ? '' : 'disabled'), 'date-picker__arrow')}>
					<Button
						label=""
						icon='far fa-chevron-left cc-btn__icon'
						onClick={() => slide('left')}
						variant="flat"
						color='secondary'
						disabled={!props.leftArrowActive}
						className="arrow__btn"
						size="sm"
					/>
				</div>
			)}
			<div className="col overflow-hidden">
				<div ref={ref} id="datePickerScrollContainer" className="date-card__wrapper row g-0 flex-nowrap">
					{datesArray.map((dateObject: ScheduleTourDateProps, index: number) => {
						return (
							<DateCard
								selectedCardIndex={props.selectedCardIndex}
								month={dateObject.month}
								day={dateObject.day}
								weekday={dateObject.weekday}
								index={index}
								key={`${dateObject.day}-${dateObject.weekday}-${index}`}
								onClick={() => {
									props.setSelectedCardIndex(index);
									props.setShowErrorMessage(false);
									props.setSelectedDate({
										day: dateObject.day,
										month: dateObject.month,
										monthNumeric: dateObject.monthNumeric,
										weekday: dateObject.weekday,
										year: dateObject.year,
									});
								}}
							/>
						);
					})}
				</div>
			</div>

			{!props.hideArrows && (
				<div className={classNames((props.rightArrowActive ? '' : 'disabled'), 'date-picker__arrow')}>
					<Button
						label=""
						icon='far fa-chevron-right cc-btn__icon'
						onClick={() => slide('right')}
						variant="flat"
						color='secondary'
						disabled={!props.rightArrowActive}
						className="arrow__btn"
						size="sm"
					/>
				</div>
			)}
		</div>
	);
};

export default DatePicker;